import { useMovieStore } from "../stores/movies"
import { useSeriesStore } from "../stores/series"
import api from "./useApi"
import axios from "axios"
import { useI18n } from "vue-i18n"
import { useUserStore } from "../stores/user"
import { url } from "../helper"
import { useTaxonomyStore } from "../stores/taxonomies"

/**
 * This composable provides functions to fetch and synchronize
 * movie and series data from the API, updating the respective
 * Pinia stores while avoiding duplicates.
 * @returns
 */
export const useDataSyncher = () => {
	// Limit per JSON:API request. Keep in sync with UI pagination if applicable.
	const apiLimit = 50

	// Pinia stores for caching/central state of movies and series
	const storeMovies = useMovieStore()
	const storeSeries = useSeriesStore()
	const userStore = useUserStore()
	const taxonomyStore = useTaxonomyStore()

	// Current locale from vue-i18n, used to build language-aware requests
	const { locale } = useI18n()

	/**
	 * Fetch both movies and series for a given category, update stores with any
	 * new items (deduped by drupal_internal__nid), and return the combined list.
	 *
	 * @param categoryId - Drupal taxonomy term ID used to filter by category
	 * @returns Promise resolving to an array with movie and series items combined
	 */
	const getCombinedTypeResults = async (categoryId: number) => {
		// Fire both requests in parallel for performance
		const [movies, series] = await Promise.all([
			api.get(
				`${url}/${locale.value}/jsonapi/node/movie?jsonapi_include=1&include=field_trailer_file,field_image,field_type,field_cast,field_features`,
				{
					params: {
						// Filter: items that belong to the given category
						"filter[field_category.meta.drupal_internal__target_id]": categoryId,
						// Filter: language-specific content
						"filter[langcode]": locale.value,
						// Pagination limit
						"page[limit]": apiLimit,
						"filter[status]": 1,
						sort: "-changed",
					},
				}
			),
			api.get(
				`${url}/${locale.value}/jsonapi/node/series?jsonapi_include=1&include=field_trailer_file,field_image,field_type,field_cast,field_features`,
				{
					params: {
						"filter[field_category.meta.drupal_internal__target_id]": categoryId,
						"filter[langcode]": locale.value,
						"filter[status]": 1,
						"page[limit]": apiLimit,
					},
				}
			),
		])

		storeMovies.state.movies.push({
			category: categoryId,
			data: movies.data.data,
		})
		storeSeries.state.series.push({
			category: categoryId,
			data: series.data.data,
		})
		const combinedResults = [...movies.data.data, ...series.data.data]
		return combinedResults
	}

	const getCombinedContinueWatchingTypeResults = async (categoryId: number) => {
		// Fire both requests in parallel for performance
		const [moviesFlag, seriesFlag] = await Promise.all([
			api.get(`/jsonapi/video_flag/video_flag`, {
				params: {
					jsonapi_include: "1",
					include: "target_entity,target_entity.field_image",
					"filter[target_entity_type]": "movie",
					"filter[profile_id]": userStore.state.selected_profile.id,
					"filter[video_flag_type]": "started",
					sort: "-changed",
				},
				headers: {
					Accept: "application/vnd.api+json",
				},
			}),
			api.get(`/jsonapi/video_flag/video_flag`, {
				params: {
					jsonapi_include: "1",
					include: "target_entity,parent_entity,parent_entity.field_image",
					"filter[target_entity_type]": "episode",
					"filter[profile_id]": userStore.state.selected_profile.id,
					"filter[video_flag_type]": "started",
					sort: "-changed",
				},
				headers: {
					Accept: "application/vnd.api+json",
				},
			}),
		])

		// const movieDetails = await Promise.all(
		// 	movies.data.data.map(async (movie) => {
		// 		const movieId = movie.relationships.target_entity.data.id
		// 		const movieDetail = await api.get(`/jsonapi/node/movie/${movieId}?jsonapi_include=1&include=field_image`, {
		// 			params: {
		// 				"filter[langcode]": locale.value,
		// 				"filter[status]": 1,
		// 			},
		// 			headers: {
		// 				Accept: "application/vnd.api+json",
		// 			},
		// 		})
		// 		return movieDetail.data.data
		// 	})
		// )

		const movies = moviesFlag.data.data.map((item) => {
			item.target_entity.flag_changed = item.changed
			return item.target_entity
		})
		const series = seriesFlag.data.data.map((item) => {
			item.parent_entity.flag_changed = item.changed
			return item.parent_entity
		})
		// Get unique parent entity IDs from the series data
		// const uniqueParentIds = Array.from(
		// 	new Set(
		// 		series.data.data.map(
		// 			(serie: { relationships: { parent_entity: { data: { id: string } } } }) =>
		// 				serie.relationships.parent_entity.data.id
		// 		)
		// 	)
		// )

		// Fetch series details for each unique parent ID
		// const seriesDetails = await Promise.all(
		// 	uniqueParentIds.map(async (seriesId) => {
		// 		const seriesDetail = await api.get(`/jsonapi/node/series/${seriesId}?jsonapi_include=1&include=field_image`, {
		// 			params: {
		// 				"filter[langcode]": locale.value,
		// 				"filter[status]": 1,
		// 			},
		// 			headers: {
		// 				Accept: "application/vnd.api+json",
		// 			},
		// 		})
		// 		return seriesDetail.data.data
		// 	})
		// )

		storeMovies.state.watchingMoviesList.push({
			category: categoryId,
			data: movies,
		})
		storeSeries.state.watchingSeriesList.push({
			category: categoryId,
			data: series,
		})

		// Return a combined array, preserving the order: movies first, then series
		return [...movies, ...series]
	}

	const getFavouriteResults = async (type: string) => {
		let results
		if (type === "all") {
			results = await api.get(
				`/jsonapi/video_flag/video_flag?jsonapi_include=1&include=target_entity,target_entity.field_image`,
				{
					params: {
						"filter[profile_id]": userStore.state.selected_profile.id,
						"filter[video_flag_type]": "favourite",
					},
				}
			)
			const realItems = Array.from(new Set(results.data.data.map((item: any) => item.target_entity)))
			storeMovies.state.profileMoviesList.push({
				category: 45,
				data: realItems.filter((item) => item.type === "node--movie"),
			})

			storeSeries.state.profileSeriesList.push({
				category: 45,
				data: realItems.filter((item) => item.type === "node--series"),
			})
		} else {
			results = await api.get(
				`/jsonapi/video_flag/video_flag?jsonapi_include=1&include=target_entity,target_entity.field_image`,
				{
					params: {
						"filter[target_entity_type]": type,
						"filter[profile_id]": userStore.state.selected_profile.id,
						"filter[video_flag_type]": "favourite",
					},
				}
			)
			const realItems = Array.from(new Set(results.data.data.map((item: any) => item.target_entity)))
			if (type === "movie") {
				storeMovies.state.profileMoviesList.push({
					category: 45,
					data: realItems,
				})
			} else {
				storeSeries.state.profileSeriesList.push({
					category: 45,
					data: realItems,
				})
			}
		}
	}

	/**
	 * Fetch a single content type (e.g., "movie" or "series") for a given category.
	 * Updates the appropriate store with the category-grouped structure.
	 *
	 * @param categoryId - Drupal taxonomy term ID used to filter by category
	 * @param type - The node type path segment, e.g. "movie" or "series"
	 * @returns Promise resolving to the results array as returned by the API
	 */
	const getSingleTypeResults = async (categoryId: number, type: string) => {
		const includeParams =
			type === "series"
				? "field_trailer_file,field_image,field_type,field_cast,field_features"
				: "field_trailer_file,field_image,field_type,field_cast,field_features"

		const res = await api.get(
			`${url}/${locale.value}/jsonapi/node/${type}?jsonapi_include=1&include=${includeParams}`,
			{
				params: {
					"filter[field_category.meta.drupal_internal__target_id]": categoryId,
					"filter[langcode]": locale.value,
					"filter[status]": 1,
					"page[limit]": apiLimit,
					...(type === "movie" ? { sort: "-changed" } : {}),
				},
			}
		)

		const results = res.data.data

		// Update the appropriate store with category-grouped structure
		if (type === "movie") {
			storeMovies.state.movies.push({
				category: categoryId,
				data: results,
			})
		} else if (type === "series") {
			storeSeries.state.series.push({
				category: categoryId,
				data: results,
			})
		}

		// Return a shallow copy of the array
		return [...results]
	}

	const getSingleContinueWatchingTypeResults = async (categoryId: number, type: string) => {
		if (type === "series") {
			type = "episode"
		}
		const includeParams = type === "episode" ? "field_image" : "field_image"

		const res = await api.get(`/jsonapi/video_flag/video_flag`, {
			params: {
				"filter[target_entity_type]": type,
				"filter[profile_id]": userStore.state.selected_profile.id,
				"filter[video_flag_type]": "started",
				sort: "-changed",
			},
			headers: {
				Accept: "application/vnd.api+json",
			},
		})

		let itemDetails
		if (type === "movie") {
			itemDetails = await Promise.all(
				res.data.data.map(async (item) => {
					const res = await api.get(
						`${locale.value}/jsonapi/node/${type}/${item.relationships.target_entity.data.id}?jsonapi_include=1&include=${includeParams}`,
						{
							params: {
								"filter[langcode]": locale.value,
								"filter[status]": 1,
								"page[limit]": apiLimit,
								...(type === "movie" ? { sort: "-changed" } : {}),
							},
						}
					)
					return res.data.data
				})
			)
		} else {
			// Get unique parent entity IDs from the series data
			const uniqueParentIds = Array.from(
				new Set(
					res.data.data
						.filter(
							(serie: { relationships: { parent_entity?: { data?: { id?: string } } } }) =>
								serie.relationships?.parent_entity?.data?.id
						)
						.map(
							(serie: { relationships: { parent_entity: { data: { id: string } } } }) =>
								serie.relationships.parent_entity.data.id
						)
				)
			)

			// Fetch series details for each unique parent ID
			itemDetails = await Promise.all(
				uniqueParentIds.map(async (seriesId) => {
					const seriesDetail = await api.get(`/jsonapi/node/series/${seriesId}?jsonapi_include=1&include=field_image`, {
						params: {
							"filter[langcode]": locale.value,
							"filter[status]": 1,
						},
						headers: {
							Accept: "application/vnd.api+json",
						},
					})
					return seriesDetail.data.data
				})
			)
		}

		const results = itemDetails

		// Update the appropriate store with category-grouped structure
		if (type === "movie") {
			storeMovies.state.watchingMoviesList.push({
				category: categoryId,
				data: results,
			})
		} else if (type === "episode") {
			storeSeries.state.watchingSeriesList.push({
				category: categoryId,
				data: results,
			})
		}

		// Return a shallow copy of the array
		return [...results]
	}

	return {
		getCombinedTypeResults,
		getCombinedContinueWatchingTypeResults,
		getFavouriteResults,
		getSingleContinueWatchingTypeResults,
		getSingleTypeResults,
	}
}
